import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import type { CampaignDto } from "@multi-chat/shared";
import { CampaignResultsSummary } from "./CampaignResultsSummary.js";
import { CampaignResultsTable } from "./CampaignResultsTable.js";
import { CampaignResultsToolbar } from "./CampaignResultsToolbar.js";
import { shouldShowCampaignResults, useCampaignResults } from "./use-campaign-results.js";
import { mapCampaignError } from "./campaigns-errors.js";

export function CampaignResultsPanel({
  organizationId,
  campaign,
}: {
  organizationId: number;
  campaign: CampaignDto;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const enabled = shouldShowCampaignResults(campaign.status);
  const results = useCampaignResults({
    organizationId,
    campaignId: campaign.id,
    campaignStatus: campaign.status,
    enabled,
  });

  if (!enabled) return null;

  const error = results.summaryError ?? results.recipientsError;
  const clearFilters = () => {
    results.setSearch("");
    results.setResultStatus("ALL");
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3" data-testid="campaign-results-panel">
      {campaign.status === "RUNNING" ? (
        <p className="m-0 text-[0.8125rem] text-muted" data-testid="campaign-results-sending-note">
          {t("campaigns.results.sendingNote")}
        </p>
      ) : null}
      {campaign.status === "COMPLETED" && results.summary?.providerUpdatesPending ? (
        <p
          className="m-0 flex items-center gap-1.5 text-[0.75rem] text-muted"
          data-testid="campaign-results-provider-pending"
        >
          <LoaderCircle
            size={14}
            className="animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          {t("campaigns.results.providerPendingNote")}
        </p>
      ) : null}

      {error ? (
        <p className="m-0 text-[0.75rem] text-error" role="alert" data-testid="campaign-results-error">
          {mapCampaignError(t, error, t("campaigns.results.loadFailed"))}
        </p>
      ) : null}

      {results.summary ? (
        <CampaignResultsSummary
          summary={results.summary}
          resultStatus={results.resultStatus}
          onSelectFilter={results.setResultStatus}
        />
      ) : results.loading ? (
        <div
          className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7"
          data-testid="campaign-results-summary-loading"
        >
          {Array.from({ length: 7 }, (_, index) => (
            <div
              key={index}
              className="h-[4.25rem] animate-pulse rounded-lg border border-line/70 bg-surface-muted motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : null}

      <CampaignResultsToolbar
        search={results.search}
        resultStatus={results.resultStatus}
        filteredTotal={results.recipients?.total ?? null}
        onSearchChange={results.setSearch}
        onResultStatusChange={results.setResultStatus}
        onClearFilters={clearFilters}
      />

      <CampaignResultsTable
        recipients={results.recipients}
        loading={results.loading}
        page={results.page}
        pageSize={results.pageSize}
        search={results.search}
        resultStatus={results.resultStatus}
        onPageChange={results.setPage}
        onPageSizeChange={results.setPageSize}
        onClearFilters={clearFilters}
      />
    </section>
  );
}
